import { client } from './client';
import { User } from '../types/cabinet.types';

export interface LoginResponse {
  token: string;
  refreshToken?: string;
  user_email: string;
  user_nicename: string;
  user_display_name: string;
  status?: string;
}

export interface RegisterPhonePayload {
  phone: string;
  email: string;
  firstName: string;
  lastName: string;
  consent1: boolean;
  consent2: boolean;
}

export interface CheckPhoneResponse {
  exists: boolean;
  has_orders: boolean;
}

export interface RequestSmsResponse {
  success?: boolean;
  no_account_yet?: boolean;
  message?: string;
}

/**
 * Логін через email/password.
 * Бекенд встановлює WP cookie — WooCommerce checkout знатиме хто купує.
 */
export const login = async (username: string, password: string): Promise<LoginResponse> => {
  const response = await client.post<LoginResponse>('/auth/login', {
    username,
    password,
  });
  return response.data;
};

/**
 * Отримує JWT для вже залогіненого WP-юзера (WP cookie → React JWT).
 * Викликається при старті якщо cabinetVars.isLoggedIn === true, але JWT ще немає.
 * Захищений nonce-ом.
 */
export const getWpSessionToken = async (): Promise<LoginResponse> => {
  const nonce =
    typeof window !== 'undefined' && window.cabinetVars?.nonce
      ? window.cabinetVars.nonce
      : '';

  if (!nonce) {
    throw new Error('No WP nonce available');
  }

  const response = await client.post<LoginResponse>('/auth/wp-session-token', { nonce });
  return response.data;
};

// Social token exchange.
export const socialLoginExchange = async (
  providerToken: string,
  provider: 'google' | 'apple'
): Promise<LoginResponse> => {
  const response = await client.post<LoginResponse>('/social_token_exchange', {
    token: providerToken,
    provider,
  });
  return response.data;
};

export interface RegisterPayload {
  email: string;
  firstName: string;
  lastName: string;
  password?: string;
  pesel?: string;
  passport?: string;
  consent1: boolean;
  consent2: boolean;
}

export const register = async (data: RegisterPayload): Promise<LoginResponse> => {
  const response = await client.post<LoginResponse>('/auth/register', data);
  return response.data;
};

/**
 * Фаза 1.1: Реєстрація через телефон.
 * Зберігає дані на бекенді (transient) і надсилає OTP.
 */
export const registerPhone = async (data: RegisterPhonePayload): Promise<void> => {
  await client.post('/auth/register-phone', data);
};

/**
 * Фаза 1.5: Перевірка чи є акаунт по номеру телефону (лише для UX).
 */
export const checkPhone = async (phone: string): Promise<CheckPhoneResponse> => {
  const response = await client.post<CheckPhoneResponse>('/auth/check-phone', { phone });
  return response.data;
};

export const logoutBackend = async (): Promise<void> => {
  const nonce =
    typeof window !== 'undefined' && window.cabinetVars?.nonce
      ? window.cabinetVars.nonce
      : '';
  await client.post('/auth/logout', { nonce }).catch(() => {
    // Ігноруємо помилки — токен і так видаляється на клієнті
  });
};

/**
 * Фаза 1.3: Запит OTP з підтримкою context.
 * context = 'login' | 'register' | 'checkout_returning'
 * При 'checkout_returning' та відсутності акаунту повертає {no_account_yet: true}.
 */
export const requestSms = async (
  phone: string,
  context: 'login' | 'register' | 'checkout_returning' = 'login'
): Promise<RequestSmsResponse> => {
  const response = await client.post<RequestSmsResponse>('/auth/request-sms', { phone, context });
  return response.data;
};

/**
 * Фаза 1.2: Верифікація OTP з підтримкою context.
 * context = 'login' | 'register'
 * При 'register': бекенд створює повноцінний акаунт з даними з transient.
 */
export const verifySms = async (
  phone: string,
  code: string,
  context: 'login' | 'register' = 'login'
): Promise<LoginResponse> => {
  const response = await client.post<LoginResponse>('/auth/verify-sms', { phone, code, context });
  return response.data;
};

export const resetPassword = async (email: string): Promise<void> => {
  await client.post('/auth/reset-password', { email });
};

export const setNewPasswordFromLink = async (key: string, login: string, password: string): Promise<void> => {
  await client.post('/users/password/set-new', { key, login, password });
};

export const sendResetPasswordLink = async (email?: string): Promise<void> => {
  // Pass lang to ensure email is in correct language
  const lang = window.cabinetVars?.lang || 'pl';
  await client.post('/users/password/reset-link', { email, lang });
};
